import React, { FocusEvent, ReactElement, useState, JSX, useEffect } from 'react';
import { Collapse, InputProps, Label } from 'reactstrap';
import { Controller, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from '../Form';
import { BaseFormElement } from '../types/types';
import classNames from 'classnames';
import { getErrorMessage } from './utils';

interface Props {
  tag: BaseFormElement['tag'];
  name: string;
  baseName?: string;
  label?: string;
  boldLabel?: boolean;
  className?: string;
  errorsProp?: Record<string, string>;
  autoComplete?: string;
  showErrors?: boolean;
  showErrorIcon?: boolean;
  disablePaste?: boolean;
  onFocus?: (e?: FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e?: FocusEvent<HTMLTextAreaElement>) => void;
  ignoreTypeSwitchInMobile?: boolean;
  ignoreTypeSwitchInDesktop?: boolean;
  rows?: number;
  cols?: number;
  subLabel?: string;
  inCollapse?: boolean;
  collapseInitiallyOpen?: boolean;
}

const FormTextarea = (props: Props & Omit<InputProps, 'tag'>): JSX.Element | null => {
  const {
    tag,
    name,
    baseName,
    label,
    boldLabel,
    className,
    errorsProp,
    autoComplete,
    disabled,
    placeholder,
    showErrors,
    showErrorIcon,
    disablePaste,
    onFocus,
    onBlur,
    rows,
    cols,
    subLabel,
    inCollapse,
    collapseInitiallyOpen
  } = props;
  const { register, errors, control, formState } = useFormContext<FieldValues>();
  const { touchedFields, dirtyFields, isSubmitted } = formState || {};
  const { _formValues } = control || {};
  const [collapseOpen, setCollapseOpen] = useState<boolean>(false);

  useEffect(() => {
    if (collapseInitiallyOpen) {
      setCollapseOpen(true);
    }
  }, [collapseInitiallyOpen]);

  if (!register) return null;

  const formErrors = errorsProp || errors;
  const fieldName = baseName ?? name;
  const fieldError = formErrors?.[fieldName];
  const errorMessage = getErrorMessage(fieldError);

  const renderField = (): ReactElement => {
    return (
      <>
        {!!subLabel && (
          <div
            className={`sub-label ${
              formErrors && showErrors && formErrors[baseName ?? name] ? 'form__error__top' : ''
            }`}
          >
            {subLabel || ''}
          </div>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }): JSX.Element => {
            return (
              <textarea
                id={`form-builder-input-${name}-${tag}`}
                /* eslint-disable-next-line */
                {...field}
                data-testid={'form-input-' + name}
                onFocus={onFocus}
                onBlur={e => {
                  field.onBlur();
                  if (onBlur) {
                    onBlur(e);
                  }
                }}
                rows={rows}
                cols={cols}
                className={`${className ?? ''} ${field.value ? 'input--filled' : ''}`}
                autoComplete={autoComplete}
                disabled={disabled}
                placeholder={placeholder}
                onPaste={(e): void => {
                  if (disablePaste) {
                    e.preventDefault();
                  }
                }}
              />
            );
          }}
        />
        {showErrors && errorMessage ? <div className="form__error">{errorMessage}</div> : null}
        {showErrorIcon && formErrors && showErrors && formErrors[baseName ?? name] && (
          <FontAwesomeIcon icon={faExclamationTriangle} />
        )}
        {!formErrors?.[baseName ?? name] &&
          (touchedFields?.[baseName ?? name] || isSubmitted) &&
          !!_formValues?.[baseName ?? name] && <span className="form-input__success" />}
      </>
    );
  };

  return (
    <div
      className={classNames('form__textarea', 'form-input-' + name, {
        'input--error': formErrors && showErrors && formErrors[baseName ?? name],
        'form-input--errors': !!formErrors?.[baseName ?? name],
        'form-input--touched': touchedFields?.[baseName ?? name],
        'form-input--dirty': dirtyFields?.[baseName ?? name],
        'form-input--form-submitted': isSubmitted
      })}
    >
      {!!label && (
        <Label
          className={`${
            formErrors && showErrors && formErrors[baseName ?? name] ? 'form__error__top' : ''
          }${boldLabel ? ' font-weight-bold' : ''}`}
          onClick={inCollapse ? (): void => setCollapseOpen(value => !value) : undefined}
        >
          <span>{label || ''}</span>
          {inCollapse && (
            <FontAwesomeIcon
              icon={faAngleDown}
              className={classNames('form__textarea__collapse', {
                'form__textarea__collapse--open': collapseOpen
              })}
            />
          )}
        </Label>
      )}
      {inCollapse ? <Collapse isOpen={collapseOpen}>{renderField()}</Collapse> : renderField()}
    </div>
  );
};

export default FormTextarea;
